import React, { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';

import { Check, ChevronDown, ChevronUp, Save, Trash, X } from "lucide-react";

import { Modal } from "./Index";
import { CustomSelect1, ICustomSelect1BaseProps, ISelectOption } from "../shared/Selects";
import { areEntitlementsEqual, isEntitlementsInvalid, ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { closeModal, getModalData, isModalActive, openModal } from "../../store/modal.slice";

import { useAddMemberToCohort, useGetCohort, useGetCohortMembers, useGetUsersListForCohortMemberSelection, useRemoveMemberFromCohort, useSaveCohortMemberEntitlements } from "../../hooks/cohorts-cax";
import { useGetEntitlementsForAdminForm } from "../../hooks/entitlements";

import { EEntitlementSource, EProduct } from "../../types/misc.enum";
import { IEntitlementsData } from "../../types/misc.interface";
import { ICohortMemberListItem } from "../../types/services.interfaces/cohorts-cax";

const MODAL_NAME = "MANAGE_COHORT_MEMBERS_MODAL";

export const ManageCohortMembersModal = () => {
    const dispatch = useDispatch();

    const isModalOpen = useSelector(isModalActive(MODAL_NAME));
    const modalData = useSelector(getModalData(MODAL_NAME));

    const [expandedUserIds, setExpandedUserIds] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [processingUserId, setProcessingUserId] = useState<string | null>(null);

    const { data: cohortDetails } = useGetCohort(modalData?.cohortId ?? null);
    const { data: cohortMembers } = useGetCohortMembers(cohortDetails?.id ?? null);
    const { data: usersListForSelection } = useGetUsersListForCohortMemberSelection(cohortDetails?.id ?? null);
    const { mutate: addMemberToCohort } = useAddMemberToCohort();
    const { mutate: removeMemberFromCohort } = useRemoveMemberFromCohort();

    const isBusy = isProcessing || !!processingUserId;

    const usersOptions = useMemo<ICohortMemberSelectOption[]>(() => {
        if (!usersListForSelection) return [];

        return usersListForSelection.map(user => ({
            value: user.user_id,
            label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || 'Unknown user',
            cohorts: user.cohorts,
            is_in_current_cohort: user.is_in_current_cohort,
        }));
    }, [usersListForSelection]);

    const onCloseModal = () => {
        dispatch(closeModal(MODAL_NAME));
    };

    const resetForm = () => {
        setExpandedUserIds([]);
        setIsProcessing(false);
        setProcessingUserId(null);
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const handleUserSelection = (option: ICohortMemberSelectOption) => {
        if (!cohortDetails?.id || !option.value) return;

        if (option.is_in_current_cohort) {
            handleRemoveUser(option.value, option.label);
        } else {
            addMemberToCohort({
                cohortId: cohortDetails.id,
                userId: option.value,
                setIsProcessing,
            });
        };
    };

    const toggleUserExpanded = (userId: string) => {
        setExpandedUserIds(prev => prev.includes(userId)
            ? prev.filter(id => id !== userId)
            : [...prev, userId]);
    };

    const handleRemoveUser = (userId: string, userLabel: string) => {
        if (isBusy || !cohortDetails?.id) return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL',
            data: {
                topupLvl: 1,
                title: 'Remove Member?',
                description: `Are you sure you want to remove "${userLabel}" from this cohort? All their entitlements will be removed as well. They will remain in any other cohorts they belong to.`,
                onConfirm: () => {
                    removeMemberFromCohort({
                        cohortId: cohortDetails.id,
                        userId,
                        setIsProcessing,
                    });
                },
            },
        }));
    };

    return (
        <Modal
            name={MODAL_NAME}
            title="Manage Cohort Members"
            baseClass="manage_cohort_members"
            topupLvl={0}
            onClose={onCloseModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
        >
            <button className="modal__close_btn" onClick={onCloseModal}><X /></button>
            <p className="color--primary text-xl text-bold mb-2xl">
                Manage members for {cohortDetails?.name ?? 'cohort'}
            </p>
            <div className="modal__cohort_members_form">
                <div className="modal__cohort_members_form__block">
                    <div className="modal__cohort_members_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Add members</p>
                    </div>
                    <div className="modal__cohort_members_form__block__right">
                        <CohortMemberSelect
                            fluid
                            placeholder="Search for a member"
                            disabled={isBusy || !cohortDetails?.id}
                            options={usersOptions}
                            onSelectToggle={option => handleUserSelection(option)}
                            cohort_id={cohortDetails?.id ?? 0}
                            selected={cohortMembers?.map(member => ({
                                value: member.user_id,
                                label: `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() || member.email || 'Unknown user',
                                cohorts: [],
                                is_in_current_cohort: true,
                            })) ?? []}
                            onRemoveUser={(option) => handleRemoveUser(option.value ?? '', option.label)}
                        />
                    </div>
                </div>

                <div className="modal__cohort_members_form__users_section">
                    <p className="text-sm text-medium color--txt-secondary mb-lg">Members</p>
                    {!cohortMembers?.length && (
                        <div className="modal__cohort_members_form__users_empty">
                            <p className="text-sm color--txt-muted">No members assigned to this cohort yet.</p>
                        </div>
                    )}
                    {!!cohortMembers?.length && (
                        <div className="modal__cohort_members_form__users_list">
                            {cohortMembers.map(user => {
                                const isExpanded = expandedUserIds.includes(user.user_id);
                                const userLabel = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || 'Unknown user';

                                return (
                                    <div key={user.user_id} className="modal__cohort_members_form__user_card">
                                        <div className="modal__cohort_members_form__user_card__header">
                                            <button
                                                type="button"
                                                className="modal__cohort_members_form__user_card__header__toggle"
                                                onClick={() => toggleUserExpanded(user.user_id)}
                                                disabled={isBusy}
                                            >
                                                <div>
                                                    <p className="text-sm text-medium">{userLabel}</p>
                                                    {!!user.email && (
                                                        <p className="text-xs color--txt-secondary">{user.email}</p>
                                                    )}
                                                </div>
                                                {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn--icon-sm btn--radius-sm btn--secondary modal__cohort_members_form__user_card__header__delete"
                                                onClick={() => handleRemoveUser(user.user_id, userLabel)}
                                                disabled={isBusy}
                                            >
                                                <Trash />
                                            </button>
                                        </div>
                                        {isExpanded && !!cohortDetails?.id && (
                                            <CohortMemberEntitlementSection
                                                user={user}
                                                cohortId={cohortDetails.id}
                                                isBusy={isBusy}
                                                isSaving={processingUserId === user.user_id}
                                                setProcessingUserId={setProcessingUserId}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

interface ICohortMemberEntitlementSectionProps {
    user: ICohortMemberListItem;
    cohortId: number;
    isBusy: boolean;
    isSaving: boolean;
    setProcessingUserId: Dispatch<SetStateAction<string | null>>;
};

const CohortMemberEntitlementSection = (props: ICohortMemberEntitlementSectionProps) => {
    const { user, cohortId, isBusy, isSaving, setProcessingUserId } = props;

    const [currentEntitlements, setCurrentEntitlements] = useState<IEntitlementsData>({
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    });

    const { data: availableEntitlements } = useGetEntitlementsForAdminForm(user.cohort_member_id.toString(), EEntitlementSource.COHORT_MEMBER);
    const { mutate: saveCohortMemberEntitlements } = useSaveCohortMemberEntitlements();

    const isDataSame = areEntitlementsEqual(currentEntitlements, user.entitlements);

    const handleSave = () => {
        if (isBusy || isDataSame || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)) {
            return;
        };

        const products = currentEntitlements.products.map(product => product.key);
        const mbtsModules = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_modules.map(module => module.id) : [];
        const mcsaModules = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_modules.map(module => module.id) : [];
        const mbtsAssessments = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_assessments : 0;
        const mcsaAssessments = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_assessments : 0;

        saveCohortMemberEntitlements({
            userId: user.user_id,
            cohortId,
            payload: {
                entitlements: {
                    products,
                    mbts_modules: mbtsModules,
                    mcsa_modules: mcsaModules,
                    mbts_assessments: mbtsAssessments,
                    mcsa_assessments: mcsaAssessments,
                },
            },
            setProcessingUserId,
        });
    };

    return (
        <div className="modal__cohort_members_form__user_card__body">
            {!availableEntitlements?.maximum_caps?.products?.length && (
                <p className="text-sm color--txt-secondary mt-lg">
                    There are no available entitlements for this member. Please setup the entitlements for cohort first. If already set, all of them might have been allocated already, please increase the limit from client/sub-client side.
                </p>
            )}
            {!!availableEntitlements?.maximum_caps?.products?.length && (
                <Fragment>
                    <ModalEntitlementForm
                        isModalOpen={false}
                        source={EEntitlementSource.COHORT_MEMBER}
                        isDivider={false}
                        isTitle={false}
                        availableEntitlements={availableEntitlements}
                        entitlements={user.entitlements}
                        onEntitlementsChange={setCurrentEntitlements}
                        isProcessing={isBusy}
                        assessmentsTooltip="Maximum number of {PRODUCT_NAME} assessments allowed for this member."
                    />
                    <div className="modal__cohort_members_form__user_card__actions">
                        <button
                            type="button"
                            className="btn btn--sm btn--radius-sm btn--primary"
                            onClick={handleSave}
                            disabled={isBusy || isDataSame || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)}
                        >
                            <Save />
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                            {isSaving && <span className="btn-loader" />}
                        </button>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

interface ICohortMemberSelectOption extends ISelectOption {
    cohorts: { cohort_id: number; cohort_name: string }[];
    is_in_current_cohort: boolean;
};

interface ICohortMemberSelect extends ICustomSelect1BaseProps {
    options: ICohortMemberSelectOption[];
    onSelectToggle: (value: ICohortMemberSelectOption) => void;
    cohort_id: number;
    selected: ICohortMemberSelectOption[];
    onRemoveUser: (option: ICohortMemberSelectOption) => void;
};

const CohortMemberSelect = (props: ICohortMemberSelect) => {
    const { placeholder, preInputIcon, postInputIcon, options, size = 'sm', fluid, disabled, onSelectToggle, cohort_id, selected, onRemoveUser } = props;

    const getDisplayedCohortLabel = (option: ICohortMemberSelectOption): string => {
        return option.cohorts
            .filter(cohort => cohort.cohort_id !== cohort_id)
            .map(cohort => cohort.cohort_name)
            .join(', ');
    };

    return (
        <CustomSelect1<ICohortMemberSelectOption>
            placeholder={placeholder}
            preInputIcon={preInputIcon}
            postInputIcon={postInputIcon}
            options={options}
            selected={selected}
            size={size}
            fluid={fluid}
            disabled={disabled}
            onSelectToggle={onSelectToggle}
            isOptionDisabled={(option) => option.is_in_current_cohort}
            onUnselect={onRemoveUser}
            renderOption={(option, forHiddenMeasure = false) => {
                const displayedCohortLabel = getDisplayedCohortLabel(option);

                return (
                    <Fragment>
                        {option.label}
                        <div className='d-flex gap-xs align-items-center'>
                            {(forHiddenMeasure || !!displayedCohortLabel) && (
                                <span className='text-xs color--txt-secondary'>{displayedCohortLabel}</span>
                            )}
                            {(forHiddenMeasure || (!!displayedCohortLabel && option.is_in_current_cohort)) && (
                                <span className='text-sm'>|</span>
                            )}
                            {(forHiddenMeasure || option.is_in_current_cohort) && <Check />}
                        </div>
                    </Fragment>
                );
            }}
        />
    );
};
